import type { Ecran, RoleCode, Session } from '../api/contrat.js';
import { cheminEcran, ecranParChemin, ecransAccessibles, libelleNavigation } from './ecrans.js';
import { ECRANS_DU_DOSSIER } from './onglets-dossier.js';
import { accueilPour, choisirLayout, LARGEUR_BUREAU, roleEffectif, type Layout } from './roles.js';

/**
 * Décision du routeur par rôle et par appareil. Fonction pure : le navigateur ne fait qu’appliquer la décision
 * (changer d’adresse ou rendre l’écran). Une page interdite renvoie à l’accueil sans dire qu’elle existe.
 */
export type Decision =
  | { type: 'rediriger'; vers: string }
  | { type: 'connexion' }
  | { type: 'lien-magique'; jeton: string }
  | { type: 'ecran'; ecran: Ecran; layout: Layout }
  | { type: 'espace-vide'; layout: Layout }
  | { type: 'introuvable'; layout: Layout };

export interface EntreeRouteur {
  chemin: string;
  session: Session | null;
  largeur: number;
}

export const CHEMIN_CONNEXION = '/connexion';
export const CHEMIN_PRESTATAIRE = '/espace-prestataire';
const JETON = /^[A-Za-z0-9_-]{4,128}$/;

const nettoyer = (chemin: string): string => {
  const sansRequete = chemin.split(/[?#]/, 1)[0] ?? '/';
  return sansRequete.length > 1 ? sansRequete.replace(/\/+$/, '') || '/' : sansRequete || '/';
};

export function resoudre({ chemin: brut, session, largeur }: EntreeRouteur): Decision {
  const chemin = nettoyer(brut);
  const jeton = chemin.startsWith('/lien/') ? chemin.slice('/lien/'.length) : null;

  if (!session) {
    if (chemin === CHEMIN_CONNEXION) return { type: 'connexion' };
    if (jeton !== null && JETON.test(jeton)) return { type: 'lien-magique', jeton };
    // Une adresse qui n'est aucun écran est introuvable, même sans session : la renvoyer vers un formulaire de mot de passe
    // inquiète l'héritier qui a mal copié son lien (PER3-03, PER-8-02). Un écran connu reste réservé (connexion).
    // L'espace du prestataire n'est pas un écran du registre, mais c'est un espace réservé connu : connexion (ARB-PRESTA-R01).
    if (chemin !== '/' && jeton === null && chemin !== CHEMIN_PRESTATAIRE && !ecranParChemin(chemin))
      return { type: 'introuvable', layout: 'externe' };
    return { type: 'rediriger', vers: CHEMIN_CONNEXION };
  }

  const role = roleEffectif(session);
  const accueil = accueilPour(role, largeur);
  const layout = choisirLayout(role, largeur);
  if (chemin === '/' || chemin === CHEMIN_CONNEXION || jeton !== null) return { type: 'rediriger', vers: accueil };
  if (chemin === CHEMIN_PRESTATAIRE) return role === 'prestataire' ? { type: 'espace-vide', layout } : { type: 'rediriger', vers: accueil };

  const ecran = ecranParChemin(chemin);
  if (!ecran) return { type: 'introuvable', layout };
  return ecransAccessibles(role).some((e) => e.id === ecran.id) ? { type: 'ecran', ecran, layout } : { type: 'rediriger', vers: accueil };
}

export interface ElementNavigation {
  id: string;
  libelle: string;
  chemin: string;
}

const RANG: Readonly<Record<string, number>> = { D: 0, M: 1, X: 2 };

/** Éléments de navigation du rôle ; sur mobile, les écrans mobiles de l’équipe passent en premier. */
export function elementsNavigation(role: RoleCode, largeur: number): ElementNavigation[] {
  const mobileDabord = largeur < LARGEUR_BUREAU;
  const rang = (id: string): number => {
    const r = RANG[id.charAt(0)] ?? 3;
    return mobileDabord && id.startsWith('M') ? -1 : r;
  };
  // Les écrans du dossier sont des onglets du dossier ouvert (onglets-dossier.ts), pas des entrées du menu.
  return ecransAccessibles(role)
    .filter((e) => !ECRANS_DU_DOSSIER.includes(e.id))
    .map((e, i) => ({ e, i }))
    .sort((a, b) => rang(a.e.id) - rang(b.e.id) || a.i - b.i)
    .map(({ e }) => ({ id: e.id, libelle: libelleNavigation(e.id), chemin: cheminEcran(e.id) }));
}
